import { ColumnLayer } from '@deck.gl/layers';

const fs = `\
#define SHADER_NAME column-layer-fragment-shader
precision highp float;
varying vec4 vColor;
varying vec4 vHighlightColor;

void main(void) {
  gl_FragColor = vColor;

  // use highlight color if this fragment belongs to the selected object.
  gl_FragColor = picking_filterHighlightColor(gl_FragColor);

  // if it is highlighted, use the provided one with Phong shading (lighting)
  if (gl_FragColor != vColor) {
    gl_FragColor = vHighlightColor;
  }

  // use picking color if rendering to picking FBO.
  gl_FragColor = picking_filterPickingColor(gl_FragColor);
}
`;

const vs = `\
#define SHADER_NAME column-layer-vertex-shader
attribute vec3 positions;
attribute vec3 normals;
attribute vec3 instancePositions;
attribute float instanceElevations;
attribute vec2 instancePositions64xyLow;
attribute vec4 instanceFillColors;
attribute vec4 instanceLineColors;
attribute float instanceStrokeWidths;
attribute vec3 instancePickingColors;
// Custom uniforms
uniform float opacity;
uniform float radius;
uniform float angle;
uniform vec2 offset;
uniform bool extruded;
uniform bool isStroke;
uniform float coverage;
uniform float elevationScale;
uniform float edgeDistance;
uniform float widthScale;
uniform float widthMinPixels;
uniform float widthMaxPixels;

// @pbeshai added
uniform vec4 color1;
uniform vec4 baseColor1;
uniform vec4 color2;
uniform vec4 baseColor2;
uniform vec4 highlightColor;
uniform vec4 highlightColorBase;
uniform vec4 toggleColor;
uniform vec4 toggleColorBase;


// Result
varying vec4 vColor;

// @pbeshai added
varying vec4 vHighlightColor;

void main(void) {

  vec4 color = isStroke ? instanceLineColors : instanceFillColors;
  // rotate primitive position and normal
  mat2 rotationMatrix = mat2(cos(angle), sin(angle), -sin(angle), cos(angle));

  // calculate elevation, if 3d not enabled set to 0
  // cylindar gemoetry height are between -1.0 to 1.0, transform it to between 0, 1
  float elevation = 0.0;
  // calculate stroke offset
  float strokeOffsetRatio = 1.0;
  if (extruded) {
    elevation = instanceElevations * (positions.z + 1.0) / 2.0 * elevationScale;
  } else if (isStroke) {
    float widthPixels = clamp(project_size_to_pixel(instanceStrokeWidths * widthScale),
      widthMinPixels, widthMaxPixels) / 2.0;
    strokeOffsetRatio += sign(positions.z) * project_pixel_size(widthPixels) / project_size(edgeDistance * coverage * radius);
  }
  // if alpha == 0.0 or z < 0.0, do not render element
  float shouldRender = float(color.a > 0.0 && instanceElevations >= 0.0);
  float dotRadius = radius * coverage * shouldRender;
  // project center of column
  vec3 centroidPosition = vec3(instancePositions.xy, instancePositions.z + elevation);
  vec2 centroidPosition64xyLow = instancePositions64xyLow;
  vec3 pos = vec3(project_size(rotationMatrix * positions.xy * strokeOffsetRatio + offset) * dotRadius, 0.);
  vec4 position_commonspace;
  gl_Position = project_position_to_clipspace(centroidPosition, centroidPosition64xyLow, pos, position_commonspace);
  // Light calculations
  // Worldspace is the linear space after Mercator projection
  vec3 normals_commonspace = project_normal(vec3(rotationMatrix * normals.xy, normals.z));
  if (extruded && !isStroke) {
    vec3 lightColor = lighting_getLightColor(color.rgb, project_uCameraPosition, position_commonspace.xyz, normals_commonspace);
    vColor = vec4(lightColor, color.a * opacity) / 255.0;
  } else {
    vColor = vec4(color.rgb, color.a * opacity) / 255.0;
  }
  // Set color to be rendered to picking fbo (also used to check for selection highlight).
  picking_setPickingColor(instancePickingColors);

  vec4 topColor;
  vec4 baseColor;
  if (color.r == 0.0 && color.g == 0.0) { // normal
    topColor = color1;
    baseColor = baseColor1;
  } else if (color.r == 1.0) { // recent
    topColor = color2;
    baseColor = baseColor2;
  } else if (color.g == 1.0) { // toggled
    topColor = toggleColor;
    baseColor = toggleColorBase;
  } else { // shouldn't happen
    topColor = vec4(color.r, color.g, color.b, 255.0);
    baseColor = vec4(0.0, 0.0, 0.0, 255.0);
  }


  // blend based on z (top to bottom)
  float tColor = clamp(positions.z, 0.0, 1.0);
  vec3 lightColor1 = lighting_getLightColor(topColor.rgb, project_uCameraPosition, position_commonspace.xyz, normals_commonspace);
  vec3 lightColor2 = lighting_getLightColor(baseColor.rgb, project_uCameraPosition, position_commonspace.xyz, normals_commonspace);
  vec3 lightColorMixed = mix(lightColor2, lightColor1, tColor);
  vColor = vec4(lightColorMixed, topColor.a * opacity) / 255.0;

  // provide highlight color with shading
  vec3 highlightLightColor1 = lighting_getLightColor(highlightColor.rgb, project_uCameraPosition, position_commonspace.xyz, normals_commonspace);
  vec3 highlightLightColor2 = lighting_getLightColor(highlightColorBase.rgb, project_uCameraPosition, position_commonspace.xyz, normals_commonspace);
  vec3 highlightLightColorMixed = mix(highlightLightColor2, highlightLightColor1, tColor);
  vHighlightColor = vec4(highlightLightColorMixed, highlightColor.a * opacity)  / 255.0;
}
`;

export default class CustomColumnLayer extends ColumnLayer {
  static componentName = 'CustomColumnLayer';

  getShaders() {
    return {
      ...super.getShaders(),
      fs,
      vs,
    };
  }

  // override draw fucntion
  draw(opts: any) {
    const {
      color1,
      baseColor1,
      color2,
      baseColor2,
      highlightColor,
      highlightColorBase = highlightColor,
      toggleColor,
      toggleColorBase,
    } = this.props;

    // add alpha to color if not defined
    color1[3] = Number.isFinite(color1[3]) ? color1[3] : 255;
    baseColor1[3] = Number.isFinite(baseColor1[3]) ? baseColor1[3] : 255;

    color2[3] = Number.isFinite(color2[3]) ? color2[3] : 255;
    baseColor2[3] = Number.isFinite(baseColor2[3]) ? baseColor2[3] : 255;

    highlightColor[3] = Number.isFinite(highlightColor[3])
      ? highlightColor[3]
      : 255;
    highlightColorBase[3] = Number.isFinite(highlightColorBase[3])
      ? highlightColorBase[3]
      : 255;

    toggleColor[3] = Number.isFinite(toggleColor[3]) ? toggleColor[3] : 255;
    toggleColorBase[3] = Number.isFinite(toggleColorBase[3])
      ? toggleColorBase[3]
      : 255;

    // add uniforms
    const uniforms = {
      ...opts.uniforms,
      color1,
      color2,
      baseColor1,
      baseColor2,
      highlightColor,
      highlightColorBase,
      toggleColor,
      toggleColorBase,
    };

    super.draw({ ...opts, uniforms });
  }
}
